import axios from "axios";

const TOKEN_KEY = "lawyer_token";
const USER_KEY = "lawyer_user";
const PENDING_EMAIL_KEY = "lawyer_pending_email";

const basePath = process.env.REACT_APP_BASE_PATH || "";

export const lawyerAuth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY)) || null;
    } catch {
      return null;
    }
  },
  setSession: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user || {}));
  },
  setUser: (user) => localStorage.setItem(USER_KEY, JSON.stringify(user || {})),
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  isLoggedIn: () => !!localStorage.getItem(TOKEN_KEY),

  // Email waiting for OTP verification — survives a refresh of the OTP page.
  getPendingEmail: () => sessionStorage.getItem(PENDING_EMAIL_KEY) || "",
  setPendingEmail: (email) => sessionStorage.setItem(PENDING_EMAIL_KEY, email),
  clearPendingEmail: () => sessionStorage.removeItem(PENDING_EMAIL_KEY),
};

// All lawyer endpoints live under /api/lawyer. The LawyerAuth middleware only
// treats a request as an API call when it expects JSON, so Accept is required.
const lawyerApi = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/lawyer`,
  headers: { Accept: "application/json" },
});

lawyerApi.interceptors.request.use((config) => {
  const token = lawyerAuth.getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

lawyerApi.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401 && lawyerAuth.getToken()) {
      lawyerAuth.clear();
      window.location.assign(`${basePath}/lawyer/login`);
    }
    return Promise.reject(error);
  }
);

const toFormData = (payload) => {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v);
  });
  return fd;
};

const webDevice = { deviceType: "web", deviceToken: "", deviceVersion: "", appVersion: "" };

export const lawyerService = {
  // payload: plain fields plus File values; attachments go as indexed keys.
  register: ({ attachments = [], ...fields }) => {
    const fd = toFormData({ ...fields, ...webDevice });
    attachments.forEach((f, i) => fd.append(`attachments[${i}]`, f));
    return lawyerApi.post("/register", fd);
  },
  login:(email) => lawyerApi.post("/login", toFormData({ email, ...webDevice })),
  verifyOTP: (email, otp) =>
    lawyerApi.post("/verifyOTP", toFormData({ email, otp, ...webDevice })),
  resendOTP: (email) => lawyerApi.post("/resendOTP", toFormData({ email })),
  logout: () => lawyerApi.post("/logout"),
  getProfile: () => lawyerApi.get("/getProfile"),
  getDashboard: () => lawyerApi.get("/getDashboard"),
  setOnline: (isOnline) =>
    lawyerApi.post("/update-profile", toFormData({ is_online: isOnline ? 1 : 0 })),
  getNotifications: () => lawyerApi.get("/getNotifications"),
  // Video calls (Agora). accept/acceptConsultation return { callId, channel, agoraAppId,
  // token, uid, user_id, caseDetails }; a consultation another lawyer claimed answers 409.
  acceptCall: (callId) => lawyerApi.post("/call/accept", { callId }),
  acceptConsultation: (callId) => lawyerApi.post("/call/accept-consultation", { callId }),
  rejectCall: (callId) => lawyerApi.post("/call/reject", { callId }),
  endCall: (callId) => lawyerApi.post("/call/end", { callId }),
  // The normal chat with this client (created if missing) — used to open it after a call.
  getCallChat: (userId) => lawyerApi.get("/call/chat/messages", { params: { user_id: userId } }),
  getCallCaseDetails: (callId) => lawyerApi.get("/call/case-details", { params: { callId } }),
  getCallHistory: (page = 1) => lawyerApi.get("/getCallHistory", { params: { page } }),
  getChats: (page = 1) => lawyerApi.get("/getChats", { params: { page, type: "normal" } }),
  setNotifications: (enabled) =>
    lawyerApi.post("/update-profile", toFormData({ notification_enable: enabled ? 1 : 0 })),
  setLanguage: (lang) => lawyerApi.post("/update-profile", toFormData({ app_lang: lang })),
  deleteAccount: () => lawyerApi.post("/delete-account"),
  getChatMessages: (chatId, page = 1) =>
    lawyerApi.get("/getChatMessages", { params: { chat_id: chatId, page } }),
  // payload: { chat_id, message?, file?, file_type?, reply_to_id? } — multipart for files.
  sendMessage: (payload) => lawyerApi.post("/sendMessage", toFormData(payload)),
  // invoice_data: { title, items: [{ title, amount }], total_amount } — JSON so the
  // nested items array survives validation.
  sendInvoice: (chatId, invoiceData) =>
    lawyerApi.post("/sendMessage", { chat_id: chatId, message: "Invoice", invoice_data: invoiceData }),
  invoiceTaskCompleted: (messageId) =>
    lawyerApi.post("/invoiceTaskCompleted", toFormData({ message_id: messageId })),
  readChatMessages: (chatId) => lawyerApi.post("/readChatMessages", toFormData({ chat_id: chatId })),

  // "Legal Platform Support" chat with the admin team (one per account).
  getSupportChat: () => lawyerApi.get("/getSupportChat"),
  getSupportChatMessages: (page = 1) => lawyerApi.get("/getSupportChatMessages", { params: { page } }),
  // payload: { message?, file?, file_type? } — multipart for files.
  sendSupportMessage: (payload) => lawyerApi.post("/sendSupportMessage", toFormData(payload)),
  readSupportMessages: () => lawyerApi.post("/readSupportMessages"),
  // Voice/video calls with the support team (SupportCallManager). type: "voice" | "video".
  acceptSupportCall: (callId) => lawyerApi.post("/acceptSupportCall", { callId }),
  // reason: "missed" (cancelled / unanswered) or "rejected" (declined); ignored once answered.
  endSupportCall: (callId, reason) => lawyerApi.post("/endSupportCall", { callId, reason }),

  // Requests (cases matching the lawyer's categories/jurisdictions) and quick questions
  getAllCases: (page = 1) => lawyerApi.get("/getAllCases", { params: { page } }),
  // Toggles: first call marks interest, a second call withdraws it.
  toggleCaseInterest: (caseId) => lawyerApi.post("/IntrestCase", toFormData({ case_id: caseId })),
  sendCaseAttachments: (caseId, files) => {
    const fd = toFormData({ case_id: caseId });
    files.forEach((f, i) => fd.append(`attachments[${i}]`, f));
    return lawyerApi.post("/InterestAttachments", fd);
  },
  getAllQuestions: (page = 1) => lawyerApi.get("/getAllQuestions", { params: { page } }),
  getQuestionDetails: (questionId) =>
    lawyerApi.get("/getQuestionDetails", { params: { question_id: questionId } }),
  answerQuestion: (questionId, answer) =>
    lawyerApi.post("/answerQuestion", toFormData({ question_id: questionId, answer })),
  // Public endpoint outside /lawyer — an absolute URL bypasses the baseURL.
  draftAnswerWithAI: (questionId, questionText) =>
    lawyerApi.post(`${process.env.REACT_APP_API_URL}/assistDescription`, {
      mode: "answer",
      question_id: questionId,
      text: (questionText || "").slice(0, 1000),
    }),

  // Clients who purchased the lawyer's services
  getSubscriptions: (status, page = 1) => lawyerApi.get("/getSubscriptions", { params: { status, page } }),
  markServiceCompleted: (userServiceId) =>
    lawyerApi.post("/markAsCompleted", toFormData({ user_service_id: userServiceId })),

  // Earnings — tab: "services" | "invoices" | "calls"; list comes back under data[tab].
  getEarnings: (tab, page = 1) =>
    lawyerApi.get("/getMyConsultations", { params: { is_earning_page: "true", tab, page } }),
  // Call plans are public; fetched without the lawyer token so prices aren't user-adjusted.
  getCallPackages: () =>
    axios.get(`${process.env.REACT_APP_API_URL}/getCallPackages`, { headers: { Accept: "application/json" } }),

  // Payouts. preference: "paypal" | "bank" (bank = not PayPal, i.e. Stripe/bank transfer).
  updatePayout: (fields) => lawyerApi.post("/updateBankDetails", toFormData(fields)),
  // A new PayPal email is saved only after the 4-digit code sent to it is confirmed.
  sendPaypalOtp: (email) => lawyerApi.post("/sendPaypalOtp", toFormData({ paypal_email: email })),
  verifyPaypalOtp: (email, otp) =>
    lawyerApi.post("/verifyPaypalOtp", toFormData({ paypal_email: email, otp })),
  getStripeStatus: () => lawyerApi.get("/stripe/status"),
  getStripeConnectLink: () => lawyerApi.post("/stripe/connect"),

  // Business services: gallery images (new File or existing URL), service lines and the
  // weekly price before platform fee. Arrays go as indexed keys so Laravel sees arrays.
  updateBusinessServices: ({ images, services, weeklyPrice }) => {
    const fd = toFormData({ weekly_price: weeklyPrice });
    images.forEach((img, i) => fd.append(`images[${i}]`, img));
    // An empty `images` field tells the API to clear the gallery.
    if (!images.length) fd.append("images", "");
    services.forEach((s, i) => fd.append(`services[${i}]`, s));
    return lawyerApi.post("/update-profile", fd);
  },
  // Profile edits go through a change request that the API applies immediately.
  // attachments: File or existing URL each.
  updateProfileRequest: ({ attachments = [], ...fields }) => {
    const fd = toFormData(fields);
    attachments.forEach((a, i) => fd.append(`attachments[${i}]`, a));
    return lawyerApi.post("/updateProfileRequest", fd);
  },
  // Public lookups shared with the registration form.
  getDropdownData: () =>
    axios.get(`${process.env.REACT_APP_API_URL}/getDropdownData`, { headers: { Accept: "application/json" } }),
  getCategories: () =>
    axios.get(`${process.env.REACT_APP_API_URL}/getCategories`, { headers: { Accept: "application/json" } }),
  getSubCategories: (categoryIds) =>
    axios.get(`${process.env.REACT_APP_API_URL}/getSubCategories`, {
      headers: { Accept: "application/json" },
      params: { categories: JSON.stringify(categoryIds) },
    }),
  draftBioWithAI: (text, category, jurisdiction) =>
    lawyerApi.post(`${process.env.REACT_APP_API_URL}/assistDescription`, {
      mode: "bio",
      text: (text || "").slice(0, 1000),
      category: (category || "").slice(0, 300) || undefined,
      jurisdiction: (jurisdiction || "").slice(0, 300) || undefined,
    }),

  // Public contact form (same endpoint as the website's Support page).
  contactUs: (payload) =>
    axios.post(`${process.env.REACT_APP_API_URL}/contact-us`, payload, { headers: { Accept: "application/json" } }),

  markNotificationRead: (id) => lawyerApi.post("/markasReadNotification", toFormData({ notification_id: id })),
  clearNotifications: () => lawyerApi.post("/clearAllNotifications"),
};

// Files the API has no upload for resolve to ".../blank.png" rather than null.
export const isBlankFile = (url) => !url || /\/blank\.png$/i.test(url);

// Some notification bodies carry email HTML (e.g. a "Download" link); show plain text.
export const plainText = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Pulls the most useful error message out of an axios error / API response.
export const apiErrorMessage = (err, fallback = "Something went wrong. Please try again.") =>
  err?.response?.data?.message ||
  (err?.response?.data?.errors && Object.values(err.response.data.errors)[0]?.[0]) ||
  fallback;

export default lawyerApi;
